import { CopyOutlined } from '@ant-design/icons';
import { DatePicker, Input, Modal, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { AccountLoginCode } from '@/stores';
import { authStore, brandStore, loginCodeStore } from '@/stores';

interface CreateLoginCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateLoginCodeDialog({
  open,
  onOpenChange,
}: CreateLoginCodeDialogProps) {
  const [displayName, setDisplayName] = useState('');
  const [userId, setUserId] = useState('');
  const [expiresAt, setExpiresAt] = useState<Dayjs | null>(null);
  const [createdCode, setCreatedCode] = useState<AccountLoginCode | null>(null);

  const activeAppId = brandStore.activeBrandAppId;
  const canSubmit = useMemo(
    () => Boolean(activeAppId && displayName.trim()),
    [activeAppId, displayName],
  );

  useEffect(() => {
    if (!open) {
      setDisplayName('');
      setUserId('');
      setExpiresAt(null);
      setCreatedCode(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!activeAppId || !displayName.trim()) {
      return;
    }

    try {
      const created = await loginCodeStore.createLoginCode({
        app_id: activeAppId,
        user_id: userId.trim() || undefined,
        display_name: displayName.trim(),
        expires_at: expiresAt ? expiresAt.toISOString() : undefined,
        created_by: authStore.user?.user_id,
      });
      setCreatedCode(created);
      message.success('登录码创建成功');
    } catch (error) {
      console.error('创建登录码失败:', error);
      message.error('创建登录码失败，请稍后重试');
    }
  };

  const handleCopy = async () => {
    if (!createdCode?.plainCode) {
      return;
    }
    try {
      await navigator.clipboard.writeText(createdCode.plainCode);
      message.success('登录码已复制');
    } catch (error) {
      console.error('复制登录码失败:', error);
      message.error('复制失败，请手动复制');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => onOpenChange(false)}
      title={createdCode ? '登录码已创建' : '新增登录码'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {createdCode ? '关闭' : '取消'}
          </Button>
          {!createdCode ? (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || loginCodeStore.creating}
            >
              {loginCodeStore.creating ? '创建中...' : '创建登录码'}
            </Button>
          ) : (
            <Button onClick={handleCopy}>
              <CopyOutlined className="mr-2" />
              复制明文码
            </Button>
          )}
        </div>
      }
      centered
      width={640}
    >
      {createdCode ? (
        <div className="space-y-4 pt-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-medium text-emerald-800">
              明文登录码只会展示这一次，请及时保存。
            </p>
            <div className="mt-3 rounded-lg bg-white px-4 py-3 font-mono text-lg font-semibold tracking-[0.2em] text-slate-900">
              {createdCode.plainCode}
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">展示名</p>
              <p className="mt-1 font-medium text-foreground">
                {createdCode.displayName}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">绑定用户ID</p>
              <p className="mt-1 font-mono text-sm text-foreground">
                {createdCode.userId || '创建登录码时自动建号并绑定'}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">掩码展示</p>
              <p className="mt-1 font-mono text-sm text-foreground">
                {createdCode.codeMasked}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">过期时间</p>
              <p className="mt-1 text-foreground">
                {createdCode.expiresAt
                  ? dayjs(createdCode.expiresAt).format('YYYY-MM-DD HH:mm:ss')
                  : '长期有效'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="displayName">展示名 *</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="例如：张三 / Design QA / 渠道同学"
              maxLength={64}
            />
            <p className="text-xs text-muted-foreground">
              首次用登录码登录创建账号时，会把这个名字写入用户昵称。
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="userId">已有用户ID（可选）</Label>
            <Input
              id="userId"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              placeholder="已有 account 用户可直接绑定；留空则创建登录码时自动建号"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiresAt">过期时间（可选）</Label>
            <DatePicker
              id="expiresAt"
              showTime
              value={expiresAt}
              onChange={(value) => setExpiresAt(value)}
              className="w-full"
              placeholder="不填则长期有效"
              disabledDate={(current) =>
                Boolean(current && current < dayjs().startOf('day'))
              }
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
